// socket.io-client emits `connect_error` with an `Error` whose engine-level
// variants ("websocket error", "timeout") carry extra `type`/`description`
// fields. `JSON.stringify` drops all of them because `Error` properties are
// non-enumerable, so logging the raw object renders as "[object Object]" in the
// RUM aggregator. This flattens the useful signal into a readable one-liner.
type ConnectionError = Error & {
  type?: string;
  description?: unknown;
};

function describeDescription(description: unknown): string {
  if (description instanceof Error) {
    return description.message;
  }
  if (description !== null && typeof description === "object") {
    return description.constructor?.name ?? "object";
  }
  return String(description);
}

// A newline inside the error message or description would split the log entry
// across lines in the RUM aggregator, so collapse any CR/LF run to a single
// space to keep the serialized error on one line.
function collapseToSingleLine(value: string): string {
  return value.replace(/[\r\n]+/g, " ");
}

export function serializeConnectionError(error: unknown): string {
  if (!(error instanceof Error)) {
    return collapseToSingleLine(typeof error === "string" ? error : "unknown error");
  }

  const connectionError = error as ConnectionError;
  const parts = [connectionError.message || "unknown error"];
  if (connectionError.type) {
    parts.push(`type=${connectionError.type}`);
  }
  if (connectionError.description !== undefined) {
    parts.push(`description=${describeDescription(connectionError.description)}`);
  }
  return collapseToSingleLine(parts.join(" | "));
}
